/**
 * FloatingAIChat — persistent floating AI assistant available on every page.
 * Opens as a slide-in panel from the bottom-right.
 * Context-aware: passes current page path to the AI for relevant answers.
 */
import { useState, useRef, useEffect, useCallback } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { projectsApi, chatApi } from '../api/client'
import { T } from '../pages/shared'

interface Message {
  role: 'user' | 'assistant'
  content: string
  confidence?: string
  follow_ups?: string[]
  ts: number
}

function TypingDots() {
  return (
    <span style={{ display:'inline-flex', gap:4, alignItems:'center', height:16 }}>
      {[0,1,2].map(i => (
        <span key={i} style={{ width:5, height:5, borderRadius:'50%', background:T.textMuted,
          animation:`fchat-bounce 1.2s ease-in-out ${i*0.2}s infinite` }} />
      ))}
      <style>{`@keyframes fchat-bounce{0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-5px)}}`}</style>
    </span>
  )
}

// Context hint based on current page
function pageContext(pathname: string): string {
  if (pathname.startsWith('/vv/project/')) return 'User is on a V&V verification project page.'
  if (pathname === '/vv')          return 'User is on the V&V Dashboard.'
  if (pathname === '/vv/projects') return 'User is on the V&V Projects list.'
  if (pathname === '/rule-studio') return 'User is on the Correction Manager.'
  if (pathname === '/rules')       return 'User is on the Rule Manager.'
  if (pathname === '/violations')  return 'User is on the Violations page.'
  if (pathname === '/corrections') return 'User is on the Correction Workbench.'
  if (pathname === '/anomaly')     return 'User is on the Anomaly Detection page.'
  if (pathname === '/trends')      return 'User is on the Trend Monitor.'
  if (pathname === '/schedules')   return 'User is on the Schedules / Automation page.'
  if (pathname === '/dashboard')   return 'User is on the main Dashboard.'
  return ''
}

const QUICK = [
  'What is the current readiness score?',
  'Which fields have the most violations?',
  'Is the gate passing this week?',
  'Top integrity violations?',
]

// ── Beautiful AI sparkle icon ─────────────────────────────────────────────────
function AIIcon({ size = 24, color = '#fff' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      {/* Sparkle star */}
      <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z"
            fill={color} opacity="0.95" />
      {/* Small sparkle top-right */}
      <path d="M19 2L19.8 4.2L22 5L19.8 5.8L19 8L18.2 5.8L16 5L18.2 4.2L19 2Z"
            fill={color} opacity="0.7" />
      {/* Small sparkle bottom-left */}
      <path d="M5 16L5.6 17.4L7 18L5.6 18.6L5 20L4.4 18.6L3 18L4.4 17.4L5 16Z"
            fill={color} opacity="0.6" />
    </svg>
  )
}

export default function FloatingAIChat() {
  const [open, setOpen]       = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput]     = useState('')
  const [loading, setLoading] = useState(false)
  const [projectId, setProjectId] = useState<string | null>(null)
  const [pulse, setPulse]     = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)
  const inputRef  = useRef<HTMLInputElement>(null)
  const location  = useLocation()

  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list().then(r => r.data),
  })

  useEffect(() => {
    if ((projects as any[]).length > 0 && !projectId) {
      setProjectId((projects as any[])[0].id)
    }
  }, [projects, projectId])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 300)
  }, [open])

  // Pulse the button briefly on page change to draw attention
  useEffect(() => {
    if (!open) { setPulse(true); setTimeout(() => setPulse(false), 1200) }
  }, [location.pathname])

  const ctx = pageContext(location.pathname)

  const sendMsg = useMutation({
    mutationFn: (text: string) =>
      chatApi.send(projectId, ctx ? `[Context: ${ctx}]\n${text}` : text,
        messages.map(m => ({ role: m.role, content: m.content }))).then(r => r.data),
    onMutate: (text) => {
      setMessages(p => [...p, { role:'user', content:text, ts:Date.now() }])
      setLoading(true)
    },
    onSuccess: (data) => {
      setMessages(p => [...p, {
        role:'assistant', content:data.answer || 'No response.',
        confidence:data.confidence, follow_ups:data.follow_up_suggestions || [],
        ts:Date.now(),
      }])
      setLoading(false)
    },
    onError: () => {
      setMessages(p => [...p, { role:'assistant', content:'⚠️ AI unavailable — check LLM_PROVIDER.', ts:Date.now() }])
      setLoading(false)
    },
  })

  const submit = useCallback((text = input.trim()) => {
    if (!text || loading) return
    setInput('')
    sendMsg.mutate(text)
  }, [input, loading, sendMsg])

  const confidenceBg: Record<string,string> = {
    high:'rgba(101,163,13,0.15)', medium:'rgba(234,179,8,0.12)', low:'rgba(239,68,68,0.12)',
  }

  return (
    <>
      {/* ── Slide-in panel ── */}
      <div style={{
        position:'fixed', bottom:0, right:0, zIndex:1200,
        width: open ? 380 : 0,
        height: open ? '100vh' : 0,
        maxHeight:'100vh',
        transition:'width 0.3s cubic-bezier(0.4,0,0.2,1)',
        overflow:'hidden',
        boxShadow: open ? '-8px 0 40px rgba(0,0,0,0.35)' : 'none',
        background: T.surface1,
        borderLeft: open ? `1px solid ${T.border2}` : 'none',
        display:'flex', flexDirection:'column',
      }}>
        {open && (
          <>
            {/* Header */}
            <div style={{ padding:'16px 18px', borderBottom:`1px solid ${T.border2}`,
              background:`linear-gradient(135deg,${T.canyon}18,${T.surface2})`,
              display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:34, height:34, borderRadius:10,
                  background:`linear-gradient(135deg,${T.canyon},${T.canyon80})`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  boxShadow:`0 4px 12px ${T.canyonGlow}` }}>
                  <AIIcon size={18} />
                </div>
                <div>
                  <div style={{ fontSize:14, fontWeight:800, color:T.textPrimary, fontFamily:T.fontDisplay }}>
                    DataSentinel AI
                  </div>
                  <div style={{ fontSize:11, color:T.textMuted }}>
                    {ctx || 'Ask anything about your data'}
                  </div>
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                {/* Project selector */}
                <select
                  value={projectId || ''}
                  onChange={e => setProjectId(e.target.value)}
                  style={{ fontSize:11, padding:'3px 6px', borderRadius:6,
                    background:T.surface3, border:`1px solid ${T.border2}`,
                    color:T.textMuted, cursor:'pointer', outline:'none', maxWidth:120 }}>
                  {(projects as any[]).map((p:any) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <button onClick={() => setMessages([])}
                  title="Clear chat"
                  style={{ background:'none', border:'none', color:T.textMuted, cursor:'pointer', fontSize:14, lineHeight:1 }}>
                  ⟳
                </button>
                <button onClick={() => setOpen(false)}
                  style={{ background:'none', border:'none', color:T.textMuted, cursor:'pointer', fontSize:20, lineHeight:1 }}>
                  ×
                </button>
              </div>
            </div>

            {/* Messages */}
            <div style={{ flex:1, overflowY:'auto', padding:'14px 16px', display:'flex', flexDirection:'column', gap:12 }}>
              {/* Empty state */}
              {messages.length === 0 && !loading && (
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                  flex:1, gap:16, paddingTop:20 }}>
                  <div style={{ width:56, height:56, borderRadius:16,
                    background:`linear-gradient(135deg,${T.canyon},${T.canyon80})`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    boxShadow:`0 8px 24px ${T.canyonGlow}` }}>
                    <AIIcon size={28} />
                  </div>
                  <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:15, fontWeight:700, color:T.textPrimary, marginBottom:4 }}>Ask Your Data</div>
                    <div style={{ fontSize:12, color:T.textMuted, lineHeight:1.5 }}>
                      Readiness scores, violations,<br/>trends and V&V compliance
                    </div>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:6, width:'100%' }}>
                    {QUICK.map(q => (
                      <button key={q} onClick={() => submit(q)}
                        style={{ padding:'8px 12px', borderRadius:8, border:`1px solid ${T.border2}`,
                          background:T.surface2, color:T.textSecondary, cursor:'pointer',
                          fontSize:12, textAlign:'left', transition:'all 0.15s' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = T.canyon; (e.currentTarget as HTMLElement).style.color = T.textPrimary }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = T.border2; (e.currentTarget as HTMLElement).style.color = T.textSecondary }}>
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Message list */}
              {messages.map((m, i) => (
                <div key={i} style={{ display:'flex', flexDirection:'column',
                  alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    maxWidth:'85%', padding:'10px 13px', borderRadius:12,
                    background: m.role === 'user'
                      ? `linear-gradient(135deg,${T.canyon},${T.canyon80})`
                      : T.surface2,
                    color: m.role === 'user' ? '#fff' : T.textPrimary,
                    fontSize:13, lineHeight:1.55,
                    borderBottomRightRadius: m.role === 'user' ? 3 : 12,
                    borderBottomLeftRadius:  m.role === 'user' ? 12 : 3,
                    boxShadow: m.role === 'user' ? `0 4px 12px ${T.canyonGlow}` : 'none',
                  }}>
                    {m.content}
                  </div>
                  {m.confidence && (
                    <div style={{ fontSize:10, padding:'2px 8px', borderRadius:99, marginTop:4,
                      background:confidenceBg[m.confidence] || 'transparent',
                      color:T.textMuted }}>
                      {m.confidence} confidence
                    </div>
                  )}
                  {m.follow_ups && m.follow_ups.length > 0 && (
                    <div style={{ display:'flex', flexDirection:'column', gap:4, marginTop:6, width:'85%' }}>
                      {m.follow_ups.slice(0,2).map((fu,fi) => (
                        <button key={fi} onClick={() => submit(fu)}
                          style={{ padding:'5px 10px', borderRadius:6, border:`1px solid ${T.border2}`,
                            background:T.surface3, color:T.textMuted, cursor:'pointer',
                            fontSize:11, textAlign:'left' }}>
                          {fu}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {loading && (
                <div style={{ display:'flex', alignItems:'flex-start' }}>
                  <div style={{ padding:'10px 14px', borderRadius:12, borderBottomLeftRadius:3,
                    background:T.surface2 }}>
                    <TypingDots />
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div style={{ padding:'12px 14px', borderTop:`1px solid ${T.border2}`, flexShrink:0,
              background:T.surface2 }}>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <input
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && submit()}
                  placeholder="Ask about readiness, violations, trends…"
                  style={{ flex:1, padding:'9px 13px', borderRadius:10,
                    border:`1px solid ${T.border2}`, background:T.surface1,
                    color:T.textPrimary, fontSize:13, outline:'none', fontFamily:T.fontBody }}
                />
                <button onClick={() => submit()} disabled={!input.trim() || loading}
                  style={{ width:38, height:38, borderRadius:10, border:'none', flexShrink:0,
                    background: !input.trim() || loading ? T.surface4 : `linear-gradient(135deg,${T.canyon},${T.canyon80})`,
                    color: !input.trim() || loading ? T.textMuted : '#fff',
                    cursor: !input.trim() || loading ? 'not-allowed' : 'pointer',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    boxShadow: input.trim() && !loading ? `0 4px 12px ${T.canyonGlow}` : 'none',
                    transition:'all 0.2s' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 2L11 13"/><path d="M22 2L15 22L11 13L2 9L22 2Z"/>
                  </svg>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Floating button ── */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          title="Ask DataSentinel AI"
          style={{
            position:'fixed', bottom:28, right:28, zIndex:1100,
            width:56, height:56, borderRadius:18,
            background:`linear-gradient(135deg,${T.canyon} 0%,#e8460a 100%)`,
            border:'none', cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center',
            boxShadow:`0 8px 28px ${T.canyonGlow}, 0 2px 8px rgba(0,0,0,0.3)`,
            transition:'transform 0.2s, box-shadow 0.2s',
            animation: pulse ? 'fchat-pulse 0.6s ease-out' : 'none',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1.08) translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 36px ${T.canyonGlow}, 0 4px 12px rgba(0,0,0,0.35)` }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; (e.currentTarget as HTMLElement).style.boxShadow = `0 8px 28px ${T.canyonGlow}, 0 2px 8px rgba(0,0,0,0.3)` }}>
          <AIIcon size={26} />
          <style>{`
            @keyframes fchat-pulse {
              0%   { transform: scale(1); }
              30%  { transform: scale(1.12); }
              60%  { transform: scale(0.97); }
              100% { transform: scale(1); }
            }
          `}</style>
        </button>
      )}
    </>
  )
}
